import {
  Directive,
  ElementRef,
  HostBinding,
  HostListener,
  Renderer2,
  forwardRef
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

/**
 * Máscara de reais no estilo "caixa eletrônico": os dígitos entram pela
 * direita e os centavos são sempre exibidos (1 → R$ 0,01; 1234 → R$ 12,34).
 *
 * Exibe "R$ 1.234,56" e entrega ao FormControl um number (1234.56) ou null
 * quando o campo está vazio.
 */
@Directive({
  selector: "input[appCurrencyMask]",
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => CurrencyMaskDirective)
    }
  ]
})
export class CurrencyMaskDirective implements ControlValueAccessor {
  private static readonly MAX_DIGITS = 15; // limite antes de perder precisão

  private readonly formatter = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  @HostBinding("style.text-align") textAlign = "right";
  @HostBinding("attr.inputmode") inputMode = "numeric";
  @HostBinding("attr.autocomplete") autocomplete = "off";

  private onChange: (value: number | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(
    private readonly el: ElementRef<HTMLInputElement>,
    private readonly renderer: Renderer2
  ) {}

  @HostListener("input", ["$event.target.value"])
  onInput(raw: string): void {
    const value = CurrencyMaskDirective.parse(raw);

    this.setDisplay(value);
    this.onChange(value);
  }

  @HostListener("blur")
  onBlur(): void {
    this.onTouched();
  }

  writeValue(value: number | string | null | undefined): void {
    this.setDisplay(CurrencyMaskDirective.toNumber(value));
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.el.nativeElement, "disabled", isDisabled);
  }

  /** Converte o texto digitado (só os dígitos contam, como centavos). */
  static parse(raw: string): number | null {
    const digits = (raw ?? "")
      .replace(/\D/g, "")
      .replace(/^0+(?=\d)/, "")
      .slice(-CurrencyMaskDirective.MAX_DIGITS);

    if (!digits) return null;

    return Number(digits) / 100;
  }

  /** Aceita number ou string numérica ("1234.56") vinda do estado. */
  static toNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === "") return null;

    const parsed =
      typeof value === "number"
        ? value
        : Number(value.toString().trim().replace(",", "."));

    return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : null;
  }

  format(value: number): string {
    return this.formatter.format(value);
  }

  private setDisplay(value: number | null): void {
    this.renderer.setProperty(
      this.el.nativeElement,
      "value",
      value === null ? "" : this.format(value)
    );
  }
}
